/**
 * Die Rückkehr aus dem Panel in der App. (28.09.2026, Ticket 2713 Stufe V2)
 *
 * Der Server leitet nach „Mit Panel verbinden“ auf `/?panel_kopplung=<ergebnis>`
 * (server/panel-kopplung.ts) — Code und Nachweis sind da schon aus der
 * Adresse. Hier steht, was die App damit tut:
 *
 *   - `anmelden`: dieser Browser hatte keine Vespera-Sitzung. Nach dem
 *     Anmelden geht es zurück auf /api/panel/kopplung/weiter, und der Server
 *     vergleicht DANN das Konto. Ein Google-Login kommt auf „/“ zurück, ohne
 *     die Adresse — darum merkt sich dieser Tab das (sessionStorage).
 *   - jedes andere Ergebnis: Einstellungen → Verbindungen, und die Karte
 *     „Panel“ sagt es (kopplungsMeldung).
 *
 * ⚠️ Nur ein Hinweis für die Oberfläche. Ob abgeholt wird, entscheidet
 *    allein der Server; eine erfundene Adresse führt höchstens zu
 *    „abgelaufen“.
 */

const KOPPLUNG_PARAM = "panel_kopplung";
export const KOPPLUNG_WEITER = "/api/panel/kopplung/weiter";
const WEITER_MERKER = "vespera:panel-kopplung-weiter";

export const KOPPLUNG_ANMELDEN_HINWEIS =
  "Melde dich mit dem Vespera-Konto an, in dem du „Mit Panel verbinden“ gewählt hast – dann wird die Verbindung abgeschlossen.";

/** Das Ergebnis aus der Adresse (nur kleine Buchstaben und _), sonst null. */
export function kopplungAusAdresse(suche: string = location.search): string | null {
  const w = new URLSearchParams(suche).get(KOPPLUNG_PARAM);
  return w && /^[a-z_]{1,20}$/.test(w) ? w : null;
}

/** Das Ergebnis aus der Adresse nehmen — ein Neuladen zeigt es nicht noch einmal. */
export function entferneKopplungAusAdresse(): void {
  const u = new URL(location.href);
  if (!u.searchParams.has(KOPPLUNG_PARAM)) return;
  u.searchParams.delete(KOPPLUNG_PARAM);
  history.replaceState(history.state, "", u.pathname + u.search + u.hash);
}

export function merkeWeiter(): void {
  try {
    sessionStorage.setItem(WEITER_MERKER, "1");
  } catch {
    /* ohne Speicher: die Adresse trägt es beim Passwort-Weg weiter */
  }
}

export function weiterGemerkt(): boolean {
  try {
    return sessionStorage.getItem(WEITER_MERKER) === "1";
  } catch {
    return false;
  }
}

export function vergissWeiter(): void {
  try {
    sessionStorage.removeItem(WEITER_MERKER);
  } catch {
    /* nichts zu vergessen */
  }
}

/** Was die App beim Start mit einer Rückkehr aus dem Panel tut. */
type StartSchritt =
  | { art: "weiter" }
  | { art: "anmelden" }
  | { art: "zeigen"; ergebnis: string }
  | { art: "nichts" };

/**
 * Die Entscheidung beim Start der App (app.ts, bootstrapAuth).
 *
 *  - angemeldet und der Merker aus der Anmeldeansicht steht: zurück zum
 *    Server (`weiter`) — er vergleicht JETZT das Konto.
 *  - angemeldet mit einem Ergebnis: zeigen (außer „anmelden“, siehe unten).
 *  - nicht angemeldet und „anmelden“ oder der Merker: die Anmeldeansicht mit
 *    Hinweis, auch wo sonst der Einzelbetrieb ohne Anmeldung gälte.
 *
 * ⚠️ Angemeldet UND „anmelden“ in der Adresse, aber ohne Merker, geht NICHT
 *    zurück zum Server: hält er eine Sitzung für ungültig, die /api/auth/me
 *    noch annimmt (gesperrtes Konto), liefe das sonst im Kreis.
 */
export function kopplungBeimStart(angemeldet: boolean, kopplung: string | null, weiter: boolean): StartSchritt {
  if (angemeldet) {
    if (weiter) return { art: "weiter" };
    if (kopplung && kopplung !== "anmelden") return { art: "zeigen", ergebnis: kopplung };
    return { art: "nichts" };
  }
  return kopplung === "anmelden" || weiter ? { art: "anmelden" } : { art: "nichts" };
}

/** Das Ergebnis, das die Karte beim nächsten Zeichnen zeigt — nur einmal. */
let wartend: string | null = null;
/** Anzeigename des gerade angemeldeten Kontos — nur, wenn die Seite ihn ohnehin kennt. */
let wartendKonto: string | null = null;

export function merkeKopplungsErgebnis(ergebnis: string, kontoName?: string | null): void {
  wartend = ergebnis;
  wartendKonto = kontoName ?? null;
}

export function nimmKopplungsKonto(): string | null {
  const k = wartendKonto;
  wartendKonto = null;
  return k;
}

/** Anzeigename des angemeldeten Kontos, wie die Seite ihn schon kennt (app.ts setzt ihn bei An- und Abmeldung). */
let angemeldet: string | null = null;

export function setzeAngemeldetesKonto(name: string | null | undefined): void {
  angemeldet = sichtbarerName(name) || null;
}

export function angemeldetesKonto(): string | null {
  return angemeldet;
}

/** Name für den Hinweis: ohne Steuer- und Anführungszeichen, kurz; leer → kein Name. */
function sichtbarerName(name?: string | null): string {
  // eslint-disable-next-line no-control-regex
  const n = (name ?? "").replace(/[\u0000-\u001f\u007f"„“”]/g, " ").replace(/\s+/g, " ").trim();
  return n.length > 40 ? `${n.slice(0, 39)}…` : n;
}

export function nimmKopplungsErgebnis(): string | null {
  const e = wartend;
  wartend = null;
  return e;
}

/** Was die Karte zu einem Ergebnis sagt; `art` steuert die Farbe. */
export function kopplungsMeldung(ergebnis: string, kontoName?: string | null): { text: string; art: "ok" | "warn" | "still" } | null {
  switch (ergebnis) {
    case "verbunden":
      return { text: "Verbunden. Vespera arbeitet jetzt mit deinem eigenen Panel-Zugang.", art: "ok" };
    case "abgelehnt":
      return { text: "Du hast die Verbindung im Panel abgelehnt – es wurde nichts verbunden.", art: "still" };
    case "nicht_moeglich":
      return {
        text: "Im Panel ist für dein Konto gerade nichts zu verbinden. Dafür braucht es das Modul „Meine Webseite“ und eine eigene Webseite – das schaltet SSI im Panel frei.",
        art: "warn",
      };
    case "anderes_konto":
    {
      const n = sichtbarerName(kontoName);
      const hier = n ? `als ein anderes Vespera-Konto angemeldet („${n}“)` : "als ein anderes Vespera-Konto angemeldet";
      return {
        text: `In diesem Browser bist du ${hier}, die Verbindung wurde aber von einem anderen Konto gestartet. Es wurde nichts verbunden. Bitte hier abmelden, mit deinem eigenen Konto anmelden und „Mit Panel verbinden“ noch einmal wählen.`,
        art: "warn",
      };
    }
    case "abgelaufen":
      return { text: "Die Verbindung ist abgelaufen. Bitte noch einmal „Mit Panel verbinden“ wählen.", art: "warn" };
    case "verbraucht":
      return {
        text: "Diese Verbindung wurde schon abgeschlossen. Steht hier nicht „verbunden“, bitte neu verbinden.",
        art: "warn",
      };
    case "nachweis":
      return { text: "Das Panel hat die Rückkehr nicht bestätigt. Bitte noch einmal verbinden.", art: "warn" };
    case "zu_viele":
      return { text: "Gerade gab es zu viele Versuche. Bitte später noch einmal.", art: "warn" };
    case "stoerung":
      return { text: "Das Panel war gerade nicht erreichbar. Bitte später noch einmal verbinden.", art: "warn" };
    default:
      return null;
  }
}
